import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/BackButton';
import AdBanner from '../components/AdBanner';
import './styles/ContentPages.css';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        q: 'What does YKI stand for?',
        a: 'YKI stands for "Yleiset kielitutkinnot", which translates to "National Certificates of Language Proficiency" in English. It is the official Finnish language proficiency test system.'
      },
      {
        q: 'Who organizes the YKI test?',
        a: 'The YKI test is organized by the Finnish National Agency for Education (Opetushallitus). Tests are administered at approved test centers across Finland, typically at universities, adult education centers, and language schools.'
      },
      {
        q: 'How often is the YKI test held?',
        a: 'YKI tests are held several times a year, usually in spring (February-May) and autumn (September-November). The exact dates vary by test center and level. Check yki.opintopolku.fi for the latest schedule.'
      },
      {
        q: 'Can I take the YKI test outside Finland?',
        a: 'Currently, YKI tests are only administered at approved test centers within Finland. If you live abroad, you would need to travel to Finland to take the test.'
      },
    ]
  },
  {
    category: 'Registration & Cost',
    questions: [
      {
        q: 'How much does the YKI test cost?',
        a: 'The registration fee is approximately 100-130 EUR depending on the level. Perustaso (basic) is typically the least expensive, while Ylin taso (advanced) costs more. Fee waivers may be available for certain groups.'
      },
      {
        q: 'Where do I register for the YKI test?',
        a: 'You can register at yki.opintopolku.fi. Create an account, choose your test level and location, and pay online. Registration typically opens 1-2 months before each test date.'
      },
      {
        q: 'Can I cancel my registration?',
        a: 'Cancellation policies vary. Generally, you can cancel up to a certain deadline before the test date and receive a partial refund. Check the specific terms when you register.'
      },
      {
        q: 'What do I need to bring to the test?',
        a: 'Bring a valid photo ID (passport, national ID card, or Finnish residence permit). You may also want to bring a pen, though writing materials are usually provided. Electronic devices are not allowed.'
      },
    ]
  },
  {
    category: 'Test Levels & Content',
    questions: [
      {
        q: 'Which YKI level do I need for Finnish citizenship?',
        a: 'For Finnish citizenship, you need to pass the YKI keskitaso (intermediate level) test with at least level 3 in all four skill areas: reading, writing, listening, and speaking. Alternatively, level 3 at the Ylin taso (advanced level) also qualifies.'
      },
      {
        q: 'What is the difference between YKI levels?',
        a: 'Perustaso (Basic) covers CEFR levels A1-A2 and tests basic everyday communication. Keskitaso (Intermediate) covers B1-B2 and tests ability to function in Finnish society. Ylin taso (Advanced) covers C1-C2 and tests near-native fluency.'
      },
      {
        q: 'How long does the YKI test take?',
        a: 'The total test duration varies by level. The basic level takes approximately 3 hours, the intermediate level around 3.5 hours, and the advanced level about 4 hours. This includes all four sections with breaks in between.'
      },
      {
        q: 'Can I retake the YKI test?',
        a: 'Yes, you can retake the YKI test as many times as you want. There is no limit on the number of attempts. Each attempt requires a new registration and fee payment.'
      },
    ]
  },
  {
    category: 'Preparation',
    questions: [
      {
        q: 'How long should I prepare for the YKI test?',
        a: 'Preparation time varies greatly depending on your current level. For keskitaso, most people study for 6-12 months if starting from scratch. If you already have A2 level Finnish, 3-6 months of focused preparation may be sufficient.'
      },
      {
        q: 'What materials should I use to prepare?',
        a: 'Use a combination of textbooks (like Suomen mestari), online resources, flashcard apps like YKI Testi, Finnish media (Yle Selkouutiset for easier Finnish), and conversation practice with native speakers. Practicing with past YKI test papers is also very helpful.'
      },
      {
        q: 'Is the YKI Testi app free?',
        a: 'Yes! YKI Testi is completely free to use. It offers vocabulary flashcards covering A1 to B2 levels, writing exercises, and speaking practice materials to help you prepare for the YKI test.'
      },
      {
        q: 'How many vocabulary words do I need to know?',
        a: 'For the basic level (A1-A2), you need approximately 1,000-2,000 words. For the intermediate level (B1-B2), aim for 3,000-5,000 words. The YKI Testi app covers vocabulary across all these levels with over 5,000 Finnish words.'
      },
    ]
  },
  {
    category: 'Results & Certificates',
    questions: [
      {
        q: 'When do I get my YKI test results?',
        a: 'Results are typically available within 6-8 weeks after the test date. You can check your results online at yki.opintopolku.fi. A certificate is mailed to your registered address.'
      },
      {
        q: 'Do YKI certificates expire?',
        a: 'No, YKI certificates do not expire. Once you pass the test, your certificate is valid indefinitely. However, some institutions may prefer a recent certificate (within the last 2-5 years).'
      },
      {
        q: 'What if I pass some sections but fail others?',
        a: 'You receive a separate score for each skill area (reading, writing, listening, speaking). If you fail one section, you keep the scores for the sections you passed. You only need to retake the sections you did not pass.'
      },
    ]
  },
];

const faqsRu = [
  {
    category: 'Общие вопросы',
    questions: [
      {
        q: 'Что означает аббревиатура YKI?',
        a: 'YKI расшифровывается как «Yleiset kielitutkinnot» — «Национальные сертификаты языковой компетентности». Это официальная система тестирования уровня владения финским языком.'
      },
      {
        q: 'Кто организует экзамен YKI?',
        a: 'Экзамен YKI организует Финское национальное агентство по образованию (Opetushallitus). Тестирование проводится в аккредитованных центрах по всей Финляндии — как правило, в университетах, центрах обучения взрослых и языковых школах.'
      },
      {
        q: 'Как часто проводится экзамен YKI?',
        a: 'Экзамены YKI проводятся несколько раз в год — обычно весной (февраль–май) и осенью (сентябрь–ноябрь). Точные даты зависят от центра тестирования и уровня. Актуальное расписание можно найти на сайте yki.opintopolku.fi.'
      },
      {
        q: 'Можно ли сдать экзамен YKI за пределами Финляндии?',
        a: 'В настоящее время экзамены YKI проводятся только в аккредитованных центрах на территории Финляндии. Если вы живёте за рубежом, вам потребуется приехать в Финляндию для прохождения теста.'
      },
    ]
  },
  {
    category: 'Регистрация и стоимость',
    questions: [
      {
        q: 'Сколько стоит экзамен YKI?',
        a: 'Регистрационный взнос составляет приблизительно 100–130 евро в зависимости от уровня. Perustaso (базовый уровень) обычно стоит меньше, Ylin taso (продвинутый уровень) — дороже. Для отдельных категорий участников возможно освобождение от оплаты.'
      },
      {
        q: 'Где зарегистрироваться на экзамен YKI?',
        a: 'Регистрация доступна на сайте yki.opintopolku.fi. Создайте аккаунт, выберите уровень и место проведения экзамена, оплатите онлайн. Регистрация, как правило, открывается за 1–2 месяца до даты экзамена.'
      },
      {
        q: 'Можно ли отменить регистрацию?',
        a: 'Условия отмены различаются. В большинстве случаев можно отменить регистрацию до определённого срока и получить частичный возврат средств. Уточняйте конкретные условия при регистрации.'
      },
      {
        q: 'Что нужно взять с собой на экзамен?',
        a: 'Возьмите действительный документ, удостоверяющий личность с фотографией (паспорт, национальное удостоверение личности или вид на жительство в Финляндии). Также можно взять ручку, хотя письменные принадлежности обычно предоставляются. Электронные устройства не допускаются.'
      },
    ]
  },
  {
    category: 'Уровни и содержание экзамена',
    questions: [
      {
        q: 'Какой уровень YKI нужен для получения финского гражданства?',
        a: 'Для получения гражданства Финляндии необходимо сдать экзамен YKI keskitaso (средний уровень) с результатом не ниже 3 по всем четырём навыкам: чтению, письму, аудированию и говорению. Также подходит уровень 3 на Ylin taso (продвинутом уровне).'
      },
      {
        q: 'В чём разница между уровнями YKI?',
        a: 'Perustaso (базовый) соответствует уровням A1–A2 по CEFR и проверяет навыки повседневного общения. Keskitaso (средний) охватывает B1–B2 и оценивает способность функционировать в финском обществе. Ylin taso (продвинутый) соответствует C1–C2 и проверяет владение языком на уровне, близком к носителю.'
      },
      {
        q: 'Сколько длится экзамен YKI?',
        a: 'Общая продолжительность экзамена зависит от уровня. Базовый уровень занимает около 3 часов, средний — примерно 3,5 часа, продвинутый — около 4 часов. Это время включает все четыре раздела с перерывами между ними.'
      },
      {
        q: 'Можно ли пересдать экзамен YKI?',
        a: 'Да, экзамен YKI можно пересдавать неограниченное количество раз. Каждая попытка требует новой регистрации и оплаты взноса.'
      },
    ]
  },
  {
    category: 'Подготовка',
    questions: [
      {
        q: 'Сколько времени нужно на подготовку к экзамену YKI?',
        a: 'Время подготовки сильно зависит от вашего текущего уровня. Для keskitaso большинство людей занимается 6–12 месяцев с нуля. Если у вас уже есть уровень A2 по финскому, достаточно 3–6 месяцев целенаправленной подготовки.'
      },
      {
        q: 'Какие материалы использовать для подготовки?',
        a: 'Используйте сочетание учебников (например, Suomen mestari), онлайн-ресурсов, приложений для карточек вроде YKI Testi, финских СМИ (Yle Selkouutiset для более простого языка) и разговорной практики с носителями. Очень полезно также тренироваться с реальными заданиями прошлых экзаменов YKI.'
      },
      {
        q: 'Приложение YKI Testi бесплатное?',
        a: 'Да! YKI Testi полностью бесплатно. В нём есть словарные карточки, охватывающие уровни от A1 до B2, письменные упражнения и материалы для практики говорения, которые помогут вам подготовиться к экзамену YKI.'
      },
      {
        q: 'Сколько слов нужно знать?',
        a: 'Для базового уровня (A1–A2) необходимо знать приблизительно 1 000–2 000 слов. Для среднего уровня (B1–B2) стремитесь к 3 000–5 000 слов. Приложение YKI Testi охватывает словарный запас всех этих уровней — более 5 000 финских слов.'
      },
    ]
  },
  {
    category: 'Результаты и сертификаты',
    questions: [
      {
        q: 'Когда я получу результаты экзамена YKI?',
        a: 'Результаты обычно становятся доступны в течение 6–8 недель после даты экзамена. Проверить их можно онлайн на сайте yki.opintopolku.fi. Сертификат отправляется по почте на ваш зарегистрированный адрес.'
      },
      {
        q: 'Истекает ли срок действия сертификата YKI?',
        a: 'Нет, сертификаты YKI не имеют срока действия. После успешной сдачи экзамена ваш сертификат действителен бессрочно. Тем не менее некоторые организации могут предпочесть свежий сертификат (выданный в последние 2–5 лет).'
      },
      {
        q: 'Что будет, если я сдам одни разделы, но не сдам другие?',
        a: 'Вы получаете отдельную оценку за каждый навык (чтение, письмо, аудирование, говорение). Если вы не сдали один раздел, оценки за успешно пройденные разделы сохраняются. Пересдавать нужно только те разделы, которые вы не прошли.'
      },
    ]
  },
];

const FaqPage = () => {
  const { i18n } = useTranslation();
  const isRu = i18n.language === 'ru';

  const [openIndex, setOpenIndex] = useState(null);

  const activeFaqs = isRu ? faqsRu : faqs;

  const toggle = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  let globalIdx = 0;

  return (
    <>
      <Helmet>
        <title>YKI Test FAQ: Common Questions About the Finnish Proficiency Test | YKI Testi</title>
        <meta name="description" content="Answers to the most common questions about the YKI Finnish language test — registration, cost, levels, preparation, results and certificates." />
        <link rel="canonical" href="https://ykitesti.fi/faq" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.flatMap(cat => cat.questions.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
          })))
        })}</script>
      </Helmet>
    <div className="content-page">
      <BackButton to="/" label={isRu ? 'Главная' : 'Home'} />
      <h1 className="content-page-title">
        {isRu ? 'Часто задаваемые вопросы' : 'Frequently Asked Questions'}
      </h1>
      <p className="content-page-subtitle">
        {isRu
          ? 'Популярные вопросы об экзамене YKI по финскому языку'
          : 'Common questions about the YKI Finnish language test'}
      </p>

      <AdBanner slot="faq-top" />

      <div className="faq-container">
        {activeFaqs.map((category, catIdx) => (
          <div key={catIdx} className="faq-category">
            <h2 className="faq-category-title">{category.category}</h2>
            {category.questions.map((item) => {
              const idx = globalIdx++;
              return (
                <div key={idx} className={`faq-item ${openIndex === idx ? 'open' : ''}`}>
                  <button
                    className="faq-question"
                    onClick={() => toggle(idx)}
                    aria-expanded={openIndex === idx}
                  >
                    <span>{item.q}</span>
                    <span className="faq-toggle" aria-hidden="true">{openIndex === idx ? '\u{2212}' : '\u{002B}'}</span>
                  </button>
                  {openIndex === idx && (
                    <div className="faq-answer">
                      <p>{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <AdBanner slot="faq-bottom" />
    </div>
    </>
  );
};

export default FaqPage;
